import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import FeedbackImg from 'assets/img/comportamento/bg-feedback-moedas.png';
import RegulamentoLayout from 'components/regulamento-layout';
import { useAuth } from 'context/AuthContext';
import { NotFound } from 'pages/NotFound';
import { actionLog } from 'services/actionlog.service';

import { canRenounceDynamics } from './communication-preferences';
import './style.scss';

const DINAMICAS = [
  { key: 'recognition', label: 'Dinâmica de Reconhecimento' },
  { key: 'task', label: 'Dinâmica de Realizações' },
  {
    key: 'actions',
    label: 'Dinâmica de Ações de Engajamento (Ações Ecoa, Desafio Semestral e Bônus de colaboração)',
  },
] as const;

export function Preference() {
  const [load, setLoad] = useState<boolean>(true);
  const [actions, setActions] = useState(true);
  const [task, setTask] = useState(true);
  const [decline, setDecline] = useState(false);
  const [recognition, setRecognition] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const { user, realCycle, refreshUser, userInfo } = useAuth();

  const modalContentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalTitleId = useId();
  const declineId = useId();
  const fieldId = useId();

  const isModalOpen = submitted && !load;

  const valores: Record<string, boolean> = { recognition, task, actions };
  const setters: Record<string, (v: boolean) => void> = { recognition: setRecognition, task: setTask, actions: setActions };

  const temRecusa = !recognition || !task || !actions;

  useEffect(() => {
    if (isModalOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isModalOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setSubmitted(false);
    }
    if (e.key === 'Tab') {
      const focusable = [closeButtonRef.current, modalContentRef.current].filter(Boolean);
      if (focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        if (first) first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        if (last) last.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.focus();
    }
  }, [isModalOpen]);

  useEffect(() => {
    async function loadData() {
      setLoad(true);

      setTask(user?.extra?.task === false ? false : true);
      setActions(user?.extra?.actions === false ? false : true);
      setRecognition(user?.extra?.recognition === false ? false : true);

      setLoad(false);
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sem confirmar o termo não se salva uma recusa.
  const saveDisabled = temRecusa && !decline;

  const currentCycle = useMemo(() => {
    if (!realCycle) return undefined;
    const cycle = realCycle.find((item: any) => item.status === 'open');

    return !!cycle ? cycle : realCycle[0];
  }, [realCycle]);

  async function handleSubmit() {
    setLoad(true);
    setSubmitted(true);

    await actionLog('me', 'accept_rules', {
      task,
      actions,
      recognition,
    });

    refreshUser();
  }

  function getText(): string {
    const parts = [];
    if (!recognition) parts.push('Reconhecimento');
    if (!task) parts.push('Realizações');
    if (!actions) parts.push('Ações de Engajamento (Ações Ecoa, Desafio Semestral e Bônus de colaboração)');

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts.join(' e ');

    return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
  }

  /*
    Quem não renuncia a dinâmica não tem o que fazer aqui: a página é a renúncia
    (AD-018). Guard restaurado ao que existia antes desta feature — `ed560909` o
    removera para abrir a página ao opt-out de canal, que saiu em 2026-08-08.

    A condição chama `canRenounceDynamics` em vez de repetir os três campos, que
    é como estava antes. Mesma expressão, com teste próprio, e uma cópia a menos
    para sair de acordo com a outra (AD-017).
  */
  if (!canRenounceDynamics({ isManager: !!userInfo?.is_manager, cadMissao: userInfo?.cad_missao, tipoRo: userInfo?.tipo_ro })) {
    return <NotFound />;
  }

  return (
    <RegulamentoLayout title="Minhas Preferências">
      <div
        className="rounded-2xl bg-brand-white px-[34px] pb-[34px] pt-[30px] shadow-[0_1px_2px_rgba(21,21,24,.08),0_1px_3px_rgba(25,25,28,.12)]"
        aria-busy={load}
      >
        {load && (
          <div role="status" aria-live="polite" className="text-center text-content-soft">
            <span className="sr-only">Carregando preferências</span>
            <span aria-hidden="true">Carregando...</span>
          </div>
        )}

        {!load && (
          <form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
            aria-label="Formulário de preferências"
          >
            <h2 className="m-0 mb-3 text-center font-display text-[22px] font-extrabold text-brand-blue-dark">
              Configurar Preferências
            </h2>

            <p className="mx-auto mb-[22px] max-w-[720px] text-center text-[13.5px] leading-relaxed text-content">
              O Joga Junto possui 3 dinâmicas que você participa. Caso você não queira participar de alguma delas, desmarque a
              dinâmica e confirme o termo de recusa. Você pode modificar essas preferências a qualquer momento até{' '}
              {currentCycle?.date_end && (
                <b className="text-brand-blue">
                  <time dateTime={new Date(currentCycle.date_end).toISOString()}>
                    {format(new Date(currentCycle.date_end), 'dd/MM/yyyy', { locale: ptBR })}
                  </time>
                </b>
              )}
            </p>

            <fieldset className="mx-auto flex max-w-[760px] flex-col gap-2.5 border-0 p-0">
              <legend className="sr-only">Selecione as dinâmicas que deseja participar</legend>

              {DINAMICAS.map(({ key, label }) => (
                <label
                  key={key}
                  htmlFor={`${fieldId}-${key}`}
                  className="flex cursor-pointer items-center gap-3 rounded-[10px] bg-[#F6F7F9] px-[18px] py-4 text-[13.5px] text-content"
                >
                  <input
                    type="checkbox"
                    id={`${fieldId}-${key}`}
                    name={key}
                    className="h-[17px] w-[17px] shrink-0 cursor-pointer accent-brand-blue"
                    checked={valores[key]}
                    onChange={e => setters[key](e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            {temRecusa && (
              <div
                className="mx-auto mt-3.5 max-w-[760px] rounded-[10px] bg-brand-yellow-light px-[18px] py-4"
                role="alert"
                aria-live="polite"
              >
                <div className="flex items-start gap-3">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--brand-blue)"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-px shrink-0"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>

                  <p className="m-0 text-[12.5px] leading-relaxed text-content">
                    Conforme Solicitação de Exclusão do Programa Joga Junto BB, os pontos obtidos nessa dinâmica não serão
                    convertidos em premiação.
                  </p>
                </div>

                <div id="decline-terms" className="mt-3 space-y-2 border-t border-black/10 pt-3 text-[12.5px] leading-relaxed">
                  <strong className="block text-brand-blue-dark">SOLICITAÇÃO DE EXCLUSÃO DO PROGRAMA JOGA JUNTO BB</strong>
                  <p className="m-0">
                    Solicito, para os devidos fins, minha exclusão do Programa Joga Junto BB referente ao Ciclo{' '}
                    {currentCycle?.title}.
                  </p>
                  <p className="m-0">
                    Estou ciente de que minha solicitação de exclusão na(s) Dinâmica(s) ({getText()}), é irrevogável e
                    irretratável e implica na perda do meu direito a participar das premiações oferecidas no Programa Joga Junto
                    BB durante o semestre acima citado.
                  </p>
                </div>

                <label htmlFor={declineId} className="mt-3 flex cursor-pointer items-center gap-2 text-[12.5px] text-content">
                  <input
                    type="checkbox"
                    id={declineId}
                    name="acceptRecognitionDecline"
                    className="h-[17px] w-[17px] shrink-0 cursor-pointer accent-brand-blue"
                    checked={decline}
                    onChange={e => setDecline(e.target.checked)}
                    aria-describedby="decline-terms"
                  />
                  Confirmo que estou ciente do termo de renúncia acima.
                </label>
              </div>
            )}

            <div className="mt-6 flex justify-center">
              <button
                type="submit"
                disabled={saveDisabled}
                aria-describedby={saveDisabled ? 'button-hint' : undefined}
                className="cursor-pointer rounded-md border-none bg-brand-yellow px-7 py-[13px] font-text text-xs font-bold uppercase tracking-wide text-brand-blue-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                Salvar Preferências
              </button>

              {saveDisabled && (
                <span id="button-hint" className="sr-only" role="alert">
                  Para salvar, confirme o termo de renúncia acima
                </span>
              )}
            </div>
          </form>
        )}
      </div>

      <ReactModal
        shouldCloseOnOverlayClick={false}
        isOpen={isModalOpen}
        onRequestClose={() => setSubmitted(false)}
        contentLabel="Preferências atualizadas"
        aria={{ labelledby: modalTitleId, describedby: `${modalTitleId}-desc` }}
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        className="outline-none"
        shouldReturnFocusAfterClose={true}
      >
        <div onKeyDown={handleKeyDown} className="w-[90%] lg:w-auto lg:max-w-[600px]">
          <div className="relative rounded-2xl bg-brand-white p-8 shadow-[0_12px_28px_rgba(0,45,75,.18)]">
            <button
              ref={closeButtonRef}
              type="button"
              className="absolute -right-4 -top-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue p-0"
              onClick={() => setSubmitted(false)}
              aria-label="Fechar modal de confirmação"
            >
              <IoIosClose color="var(--brand-yellow)" size={40} aria-hidden="true" />
            </button>

            <div ref={modalContentRef} className="flex flex-col items-center justify-center" tabIndex={-1}>
              <img src={FeedbackImg} alt="" role="presentation" className="mb-6 block w-[173px]" />

              <h2 id={modalTitleId} className="mb-3 text-center font-display text-2xl font-extrabold text-brand-blue-dark">
                Preferências Atualizadas!
              </h2>

              <p id={`${modalTitleId}-desc`} className="m-0 text-center text-sm text-content">
                Suas preferências foram atualizadas com sucesso!
              </p>
            </div>
          </div>
        </div>
      </ReactModal>
    </RegulamentoLayout>
  );
}
